import React, { useState, useEffect } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Plus, Building2, Edit2, Trash2, MapPin, Phone, User, Truck } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LocationMasterPage() {
  const { user, getAuthHeader } = useAuth();
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [editLocation, setEditLocation] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    address: '',
    contact_person: '',
    phone: '',
    is_dispatch_location: false
  });

  const fetchLocations = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/locations`, { headers: getAuthHeader() });
      setLocations(response.data);
    } catch (error) {
      console.error('Error fetching locations:', error);
      toast.error('Failed to fetch locations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLocations();
  }, []);

  const resetForm = () => {
    setFormData({
      code: '',
      name: '',
      address: '',
      contact_person: '',
      phone: '',
      is_dispatch_location: false
    });
    setEditLocation(null);
  };

  const handleOpenDialog = (location = null) => {
    if (location) {
      setEditLocation(location);
      setFormData({
        code: location.code || '',
        name: location.name || '',
        address: location.address || '',
        contact_person: location.contact_person || '',
        phone: location.phone || '',
        is_dispatch_location: location.is_dispatch_location || false
      });
    } else {
      resetForm();
    }
    setOpen(true);
  };

  const handleSubmit = async () => {
    if (!formData.code.trim()) {
      toast.error('Location code is required');
      return;
    }
    if (!formData.name.trim()) {
      toast.error('Location name is required');
      return;
    }

    try {
      if (editLocation) {
        await axios.patch(
          `${API_URL}/locations/${editLocation.id}`,
          formData,
          { headers: getAuthHeader() }
        );
        toast.success('Location updated successfully');
      } else {
        await axios.post(`${API_URL}/locations`, formData, { headers: getAuthHeader() });
        toast.success('Location created successfully');
      }
      setOpen(false);
      resetForm();
      fetchLocations();
    } catch (error) {
      console.error('Error saving location:', error);
      toast.error(error.response?.data?.detail || 'Failed to save location');
    }
  };

  const handleDelete = async () => {
    if (!selectedLocation) return;
    
    try {
      await axios.delete(`${API_URL}/locations/${selectedLocation.id}`, { headers: getAuthHeader() });
      toast.success('Location deactivated successfully');
      setOpenDelete(false);
      setSelectedLocation(null);
      fetchLocations();
    } catch (error) {
      console.error('Error deleting location:', error);
      toast.error(error.response?.data?.detail || 'Failed to deactivate location');
    }
  };

  const isAdmin = user?.is_owner;
  const activeLocations = locations.filter(l => l.is_active !== false);

  return (
    <DashboardLayout>
      <div className="space-y-6 p-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Location Master</h1>
            <p className="text-sm text-gray-500 mt-1">
              Manage physical units/locations for multi-unit operations (Metal Work, Wood Work, Assembly, etc.)
            </p>
          </div>
          {isAdmin && (
            <Button onClick={() => handleOpenDialog()} className="gap-2">
              <Plus className="h-4 w-4" />
              Add Location
            </Button>
          )}
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-500">Total Locations</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{activeLocations.length}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-500">Dispatch Points</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">
                {activeLocations.filter(l => l.is_dispatch_location).length}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-gray-500">Production Units</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-600">
                {activeLocations.filter(l => !l.is_dispatch_location).length}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Locations Table */}
        <Card>
          <CardHeader>
            <CardTitle>All Locations</CardTitle>
            <CardDescription>
              Physical units where production processes are performed
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8 text-gray-500">Loading...</div>
            ) : activeLocations.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Building2 className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p>No locations found. Add your first location to get started.</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Code</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Address</TableHead>
                    <TableHead>Contact Person</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>Dispatch Point</TableHead>
                    {isAdmin && <TableHead className="text-right">Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {activeLocations.map((location) => (
                    <TableRow key={location.id}>
                      <TableCell>
                        <Badge variant="outline" className="font-mono">
                          {location.code}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-medium">{location.name}</TableCell>
                      <TableCell className="text-gray-600 max-w-xs truncate">
                        {location.address || '-'}
                      </TableCell>
                      <TableCell>{location.contact_person || '-'}</TableCell>
                      <TableCell>{location.phone || '-'}</TableCell>
                      <TableCell>
                        {location.is_dispatch_location ? (
                          <Badge className="bg-green-100 text-green-800 border-green-200 gap-1">
                            <Truck className="h-3 w-3" />
                            Yes
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-gray-500">No</Badge>
                        )}
                      </TableCell>
                      {isAdmin && (
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleOpenDialog(location)}
                            >
                              <Edit2 className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-600 hover:text-red-700"
                              onClick={() => {
                                setSelectedLocation(location);
                                setOpenDelete(true);
                              }}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Add/Edit Dialog */}
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>{editLocation ? 'Edit Location' : 'Add New Location'}</DialogTitle>
              <DialogDescription>
                {editLocation 
                  ? 'Update the location details below.'
                  : 'Enter the details for the new location/unit.'}
              </DialogDescription>
            </DialogHeader>
            
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="code">
                    Location Code <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="code"
                    placeholder="e.g., METAL, WOOD, ASSEMBLY"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                    maxLength={20}
                  />
                  <p className="text-xs text-gray-500">Short unique identifier</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="name">
                    Location Name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="name"
                    placeholder="e.g., Metal Work Unit"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="address">Address</Label>
                <Textarea
                  id="address"
                  placeholder="Full address of the location"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  rows={2}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="contact_person">Contact Person</Label>
                  <Input
                    id="contact_person"
                    placeholder="Name of point of contact"
                    value={formData.contact_person}
                    onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    placeholder="Contact number"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  />
                </div>
              </div>

              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <Label htmlFor="dispatch" className="font-medium">Dispatch Location</Label>
                  <p className="text-xs text-gray-500">Can finished goods be dispatched from here?</p>
                </div>
                <Switch
                  id="dispatch"
                  checked={formData.is_dispatch_location}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_dispatch_location: checked })}
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleSubmit}>
                {editLocation ? 'Update' : 'Create'} Location
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={openDelete} onOpenChange={setOpenDelete}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Deactivate Location</DialogTitle>
              <DialogDescription>
                Are you sure you want to deactivate "{selectedLocation?.name}"? 
                This will not delete any associated data but the location will no longer be available for selection.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpenDelete(false)}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleDelete}>
                Deactivate
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
